import React from "react";
import { Popover, Button, message } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/free-solid-svg-icons";
import { UuidT } from "../../flatbuffers/generated/front-end/uuid";
import { formatUuidT } from "../../flatbuffers/display";

interface WizardIdHoverTagProps {
  /** The row entity's id (e.g. a bank floor, lift floor opening, or notification). */
  value: UuidT | string | number | null | undefined;
  children: React.ReactNode;
}

/**
 * Wraps `children` with a hover popover revealing the row entity's id and a
 * copy-to-clipboard button.
 *
 * For row-level entities inside dense tables -- bank floors, lift floor
 * openings, notifications -- where there's no spare column width for a
 * dedicated, always-visible id field like WizardReadOnlyId provides on the
 * single-entity forms. Renders `children` unwrapped when there is no id yet
 * (e.g. an unsaved row).
 *
 * Pass a small icon as `children` (see WizardEntityLink's faInfoCircle icon
 * for the equivalent click-triggered affordance) rather than wrapping an
 * interactive cell -- wrapping an editable Input/InputNumber would pop the
 * hover open every time the user hovers over it to click or type.
 */
export default function WizardIdHoverTag({ value, children }: WizardIdHoverTagProps) {
  const idString = formatUuidT(value);
  if (!idString) return <>{children}</>;

  return (
    <Popover
      trigger="hover"
      content={
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <span style={{ fontFamily: "monospace", fontSize: 12 }}>{idString}</span>
          <Button
            type="text"
            size="small"
            icon={<FontAwesomeIcon icon={faCopy} />}
            onClick={() => {
              navigator.clipboard.writeText(idString);
              message.success("ID copied to clipboard");
            }}
          />
        </div>
      }
    >
      <span style={{ cursor: "help" }}>{children}</span>
    </Popover>
  );
}
